import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useAppModals } from "../../../../app/state/modalSlice/hook/use-app-modals";
import { Chat } from "../../../../common/Chat";
import { AttachedFileModal } from "../../../Modals/AttachedFileModal";
import { UPLOADED_FILE_EXTENSIONS } from "../../../Modals/AttachedFileModal/constants";
import { useSchemasChat } from "../../hooks/use-schemas-chat";
import { AppUnderlinedTitle } from "../../../../shared/components/AppUnderlinedTitle";
import { AppIconButton } from "../../../../shared/components/AppIconButton";
import { BigXIcon } from "../../../../shared/icons/BigXIcon";
import { AppButton } from "../../../../shared/components/AppButton";
import { SchemaChatMode } from "../SchemaChatMode";
import { useFileTransferModal } from "../../../Modals/FileTransferModal/hook/use-file-transfer-modal";
import { FileTransferModal } from "../../../Modals/FileTransferModal";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { TFileTransferInfo } from "../../../Modals/FileTransferModal/types";
import { selectUserArtifactGroup } from "../../../Auth/redux/selectors";
import { ClearIcon } from "../../../../shared/icons/ClearIcon";
import {
  selectIsMessageLoadingById,
  selectMessagesBySchemaId,
  selectSchemaValue,
} from "../../redux/selectors";
import styles from "./styles.module.css";
import { SchemaTimer } from "../SchemaTimer";
import { throttle } from "throttle-debounce";
import { ChatDrawer } from "../../../../common/Chat/Components/ChatDrawer";

interface Props {
  isChatOpen: boolean;
  loadXml: (xml: string) => void;
  exportAsBase64: () => Promise<string>;
  handleAutosaveThrottle: throttle<(xml: string) => void>;
  closeChat: () => void;
  className?: string;
}

export const SchemasChat = ({
  isChatOpen,
  closeChat,
  handleAutosaveThrottle,
  exportAsBase64,
  loadXml,
  className,
}: Props) => {
  const {
    message,
    fileName,
    selectedId,
    isFileLoading,
    inputRef,
    withXml,
    chatMode,
    fileContent,
    selectedObject,
    handleArtifactsClick,
    handleClearChat,
    handleChangeChatMode,
    handleUploadCurrentSchema,
    handleDeleteFileTag,
    handleFileUpload,
    handleChangeMessage,
    handleSendKeyDown,
    handleSendMessage,
  } = useSchemasChat(loadXml, exportAsBase64, handleAutosaveThrottle);
  const value = useAppSelector(selectSchemaValue);
  const [info, setInfo] = useState<TFileTransferInfo | null>(null);
  const { handleCloseModal, handleOpenModal } = useAppModals();
  const { handleClick } = useFileTransferModal(info);
  const artifacts = useAppSelector(selectUserArtifactGroup);
  const isMessageLoading = useAppSelector((state) =>
    selectIsMessageLoadingById(state, selectedId ?? null),
  );
  const currentMessages = useAppSelector((state) =>
    selectMessagesBySchemaId(state, selectedId ?? null),
  );
  const { t } = useTranslation();
  const schemasMessages = useMemo(() => {
    return chatMode === "generate_schema"
      ? currentMessages
      : (value?.schemas_object?.messages ?? []);
  }, [currentMessages, chatMode, value?.schemas_object?.messages]);

  const placeholder = useMemo(() => {
    return chatMode === "generate_schema"
      ? t("schemas.xml_chat_placeholder")
      : t("schemas.img_chat_placeholder");
  }, [chatMode, i18next.language]);

  const handleFileClick = useCallback(
    (url: string, filename: string) => {
      setInfo({
        url: url,
        name: `${selectedObject?.friendly_name ?? "схема"}.png`,
      });
    },
    [selectedObject],
  );

  useEffect(() => {
    if (!info) return;

    if (artifacts) {
      handleOpenModal("file_transfer_modal", null);
    } else {
      handleClick("download");
    }
  }, [info]);

  return (
    <>
      <ChatDrawer
        isChatOpen={isChatOpen}
        handleCloseChat={closeChat}
        headerContent={
          <SchemaChatMode
            chatMode={chatMode ?? "generate_schema"}
            onChange={handleChangeChatMode}
          />
        }
      >
        <Chat
          inputRef={inputRef}
          messages={schemasMessages}
          userMessage={message}
          fileName={withXml ? fileContent : (fileName ?? "")}
          inputStyle={"elevated"}
          onChangeUserMessage={handleChangeMessage}
          handleSendMessage={handleSendMessage}
          handleKeyDownMessage={handleSendKeyDown}
          handleFileUpload={handleFileUpload}
          handleOpenAttachedFileModal={handleOpenModal}
          isMessageLoading={
            isMessageLoading && isMessageLoading.isThreadMessageLoading
              ? (isMessageLoading.isThreadMessageLoading ?? false)
              : chatMode === "generate_image"
                ? (isMessageLoading?.isCompletionMessageLoading ?? false)
                : (isMessageLoading?.isResponseMessageLoading ?? false)
          }
          isFileLoading={isFileLoading}
          onDeleteFilaTag={handleDeleteFileTag}
          onFileClick={handleFileClick}
          additionalOption={
            <AppButton
              onClick={handleUploadCurrentSchema}
              buttonType={"text"}
              className={styles["menu-btn"]}
            >
              Текущая схема
            </AppButton>
          }
          additionalAction={
            <AppIconButton onClick={handleClearChat}>
              <ClearIcon />
            </AppIconButton>
          }
          loadingMessage={
            chatMode === "generate_image" ? (
              <SchemaTimer objectId={selectedId ?? ""} />
            ) : (
              ""
            )
          }
          inputPlaceholder={placeholder}
          className={styles["chat"]}
        />
      </ChatDrawer>
      <AttachedFileModal
        extensions={UPLOADED_FILE_EXTENSIONS}
        onClose={handleCloseModal}
        handleClick={handleArtifactsClick}
        multiple={false}
      />
      <FileTransferModal
        closeModal={handleCloseModal}
        handleActions={handleClick}
      />
    </>
  );
};
